import { ref } from 'vue';
import { Filters } from '../store/filter.interface';

// This function is used to build a query string based on the filters provided.
// It takes an object of filters and a query string as input and returns the modified query string.

// The function will return the query string with the filters applied.
// For example: WHERE name = 'John' AND age > 30 ORDER BY name DESC LIMIT 10 OFFSET 0
// The function will also handle the case where no filters are provided and return the original query string.

export function useQueryBuilder(filters: Filters, query: string) {
   const queryString = ref(query);

   // all filters
   const searchQuery = ref<string>('');
   const collectionQuery = ref<string>('');
   // filterData
   const releaseQuery = ref<string>('');
   const watchStatusQuery = ref<string>('');
   const genreQuery = ref<string>('');
   const scoreQuery = ref<string>('');
   const savedByQuery = ref<string>('');
   const runtimeQuery = ref<string>('');
   const orderQuery = ref<string>('');

   function buildQuery() {
      // Reset query string
      queryString.value = query;

      // Collection query
      if (filters.collection) {
         if (!queryString.value.includes('WHERE')) queryString.value += ' WHERE ';
         collectionQuery.value = `uc.cinema_type = "${filters.collection === 'movies' ? 'movie' : 'serie'}"`;

         // check last query string is WHERE
         if (queryString.value.endsWith('WHERE ')) {
            queryString.value += ` ${collectionQuery.value} `;
         } else {
            queryString.value += `AND ${collectionQuery.value} `;
         }
      }

      // Build search query
      if (filters.search) {
         searchQuery.value = `c.title LIKE '%${filters.search}%'`;
         queryString.value += `AND ${searchQuery.value} `;
      }

      // Build filter queries
      if (filters.filterData) {
         filters.filterData.forEach((filter) => {
            switch (filter.key) {
               case 'release_date':
                  if (filter.min_value && filter.max_value) {
                     releaseQuery.value = `c.release_date BETWEEN '${filter.min_value}' AND '${filter.max_value}'`;
                  }
                  break;
               case 'watch_status':
                  if (filter.selected_ids && filter.selected_ids.length > 0) {
                     watchStatusQuery.value = `uc.watch_status_id IN (${filter.selected_ids.join(',')})`;
                  }
                  break;
               case 'genre':
                  if (filter.selected_ids && filter.selected_ids.length > 0) {
                     genreQuery.value = `c.genre_ids LIKE '%${filter.selected_ids.join(',')}%'`;
                  }
                  break;
               case 'score':
                  if (filter.value && +filter.value.value > 0) {
                     scoreQuery.value = `c.vote_average >= ${filter.value.value}`;
                  }
                  break;
               case 'saved_by': {
                  const savedByConditions: string[] = []; // Temporary array to store conditions

                  if ((filter.selected_ids || []).length === 1) {
                     // If multiple IDs are selected, check all combinations
                     filter.selected_ids?.forEach((id) => {
                        const otherOptions = filter.options.filter((option) => option.id !== id);
                        otherOptions.forEach((otherOption) => {
                           savedByConditions.push(`uc.user_id = ${id} AND uc.partner_id = ${otherOption.id}`);
                        });
                     });
                  } else {
                     // If only one ID is selected, check the first two options
                     // This assumes that the first two options are the ones to be compared
                     // and that the selected ID is one of them.
                     const option = filter.options[0];
                     const otherOption = filter.options[1];
                     if (option && otherOption)
                        savedByConditions.push(
                           `(uc.user_id = ${option.id} AND uc.partner_id = ${otherOption.id})`,
                           `(uc.user_id = ${otherOption.id} AND uc.partner_id = ${option.id})`,
                        );
                  }

                  // Join all conditions with ' OR ' and wrap in parentheses
                  if (savedByConditions.length > 0) {
                     savedByQuery.value = `(${savedByConditions.join(' OR ')})`;
                  }
                  break;
               }
               case 'runtime':
                  if (filter.min_value && filter.max_value) {
                     runtimeQuery.value = `c.runtime BETWEEN ${filter.min_value} AND ${filter.max_value}`;
                  }
                  break;
               default:
                  break;
            }
         });
      }

      // Combine all queries
      queryString.value += `${releaseQuery.value ? ` AND ${releaseQuery.value}` : ''}${watchStatusQuery.value ? ` AND ${watchStatusQuery.value}` : ''}${genreQuery.value ? ` AND ${genreQuery.value}` : ''}${scoreQuery.value ? ` AND ${scoreQuery.value}` : ''}${savedByQuery.value ? ` AND ${savedByQuery.value}` : ''}${runtimeQuery.value ? ` AND ${runtimeQuery.value}` : ''}`;
      // Remove extra AND if present
      queryString.value = queryString.value.replace(/AND\s+$/, '');

      // Add order by clause
      if (filters.currentPage) {
         const orderFilterData = filters.filterData.find((filter) => filter.key === 'order');
         if (orderFilterData) {
            switch (orderFilterData.selected_id) {
               case 'popularity.asc':
                  orderQuery.value = 'ORDER BY c.popularity ASC';
                  break;
               case 'popularity.desc':
                  orderQuery.value = 'ORDER BY c.popularity DESC';
                  break;
               case 'primary_release_date.asc':
                  orderQuery.value = 'ORDER BY c.release_date ASC';
                  break;
               case 'primary_release_date.desc':
                  orderQuery.value = 'ORDER BY c.release_date DESC';
                  break;
               case 'vote_average.asc':
                  orderQuery.value = 'ORDER BY c.vote_average ASC';
                  break;
               case 'vote_average.desc':
                  orderQuery.value = 'ORDER BY c.vote_average DESC';
                  break;
               case 'vote_count.asc':
                  orderQuery.value = 'ORDER BY c.vote_count ASC';
                  break;
               case 'vote_count.desc':
                  orderQuery.value = 'ORDER BY uc.vote_count DESC';
                  break;
               case 'rank.asc':
                  orderQuery.value = 'ORDER BY uc.rank ASC';
                  break;
               case 'rank.desc':
                  orderQuery.value = 'ORDER BY uc.rank DESC';
                  break;
               case 'watch_status.asc':
                  orderQuery.value = 'ORDER BY ws.rank ASC';
                  break;
               case 'watch_status.desc':
                  orderQuery.value = 'ORDER BY ws.rank DESC';
                  break;
               default:
                  orderQuery.value = 'ORDER BY uc.id DESC';
                  break;
            }
         } else {
            orderQuery.value = 'ORDER BY uc.id DESC';
         }

         orderQuery.value = `${orderQuery.value} LIMIT 20 OFFSET ${(filters.currentPage - 1) * 20}`;
         queryString.value += ` ${orderQuery.value}`;
      }

      return queryString.value;
   }

   return {
      queryString,
      buildQuery,
   };
}
